/**
 * Code lens
 *
 * Overlays clickable hints above declarations:
 *   - module:    "N dependents" → archlang.showDependents
 *   - process:   "View flow diagram" → archlang.openProcessFlow
 *   - view:      "Open preview" → archlang.openViewPreview
 *   - surface:     "N references" → archlang.showDependents (parent qname)
 *   - interface: "N references" → archlang.showDependents (qualified iface)
 *   - type:      "N instances" or "extended by N kinds" → archlang.showDependents
 *
 * Reference counts come from the workspace ReferenceIndex.
 */

import type { CodeLens, Range } from "vscode-languageserver";
import type {
  SurfaceDecl,
  InterfaceDecl,
  ModuleDecl,
  SourceSpan,
} from "@archlang/engine";
import type { FileState } from "../workspace/workspace.js";
import type { SymbolTable } from "../workspace/symbol-table.js";
import type { ReferenceIndex } from "../workspace/reference-index.js";
import { toLspRange } from "../lsp-types.js";

export function provideCodeLens(
  file: FileState,
  symbols: SymbolTable,
  refs: ReferenceIndex
): CodeLens[] {
  const out: CodeLens[] = [];

  for (const top of file.file.declarations) {
    if (top.kind === "module") {
      addModuleLens(top.decl, symbols, refs, out);
    } else if (top.kind === "process") {
      const p = top.decl;
      if (!p.source) continue;
      out.push({
        range: firstLine(p.source),
        command: {
          title: "View flow diagram",
          command: "archlang.openProcessFlow",
          arguments: [p.name],
        },
      });
    } else if (top.kind === "view") {
      const v = top.decl;
      if (!v.source) continue;
      out.push({
        range: firstLine(v.source),
        command: {
          title: "Open preview",
          command: "archlang.openViewPreview",
          arguments: [v.name],
        },
      });
    } else if (top.kind === "type") {
      const t = top.decl;
      if (!t.source) continue;
      const reg = symbols;
      const instanceCount = reg
        .all()
        .filter((s) => s.typeName === t.name && s.kind !== "type").length;
      const extenders = reg
        .all()
        .filter((s) => s.kind === "type" && s.typeBase === t.name);
      const title =
        extenders.length > 0
          ? `${instanceCount} instance${instanceCount === 1 ? "" : "s"} · extended by ${extenders.length}`
          : `${instanceCount} instance${instanceCount === 1 ? "" : "s"}`;
      out.push({
        range: firstLine(t.source),
        command: {
          title,
          command: "archlang.showDependents",
          arguments: [t.name],
        },
      });
    }
  }

  return out;
}

function addModuleLens(
  m: ModuleDecl,
  symbols: SymbolTable,
  refs: ReferenceIndex,
  out: CodeLens[],
  ownerQname?: string
): void {
  if (!m.source) return;
  const qname = ownerQname ? `${ownerQname}.${m.name}` : m.name;
  // Resolve from the DECL's own file (v0.13): a twin name counts ITS refs.
  const sym = symbols.resolve(qname, m.source.file);
  const count = sym ? refs.referencesTo(sym.id).length : 0;
  out.push({
    range: firstLine(m.source),
    command: {
      title: `${count} dependent${count === 1 ? "" : "s"}`,
      command: "archlang.showDependents",
      arguments: [qname],
    },
  });

  for (const f of m.body.surfaces) addSurfaceLens(f, qname, symbols, refs, out);
  for (const i of m.body.interfaces) addInterfaceLens(i, qname, symbols, refs, out);
  for (const sub of m.body.modules) addModuleLens(sub, symbols, refs, out, qname);
}

function addSurfaceLens(
  f: SurfaceDecl,
  ownerQname: string,
  symbols: SymbolTable,
  refs: ReferenceIndex,
  out: CodeLens[]
): void {
  if (!f.source) return;
  const qname = `${ownerQname}.${f.name}`;
  const sym = symbols.resolve(qname, f.source.file);
  const count = sym ? refs.referencesTo(sym.id).length : 0;
  out.push({
    range: firstLine(f.source),
    command: {
      title: `${count} reference${count === 1 ? "" : "s"}`,
      command: "archlang.showDependents",
      arguments: [qname],
    },
  });
  for (const sub of f.body.surfaces) addSurfaceLens(sub, qname, symbols, refs, out);
  for (const i of f.body.interfaces) addInterfaceLens(i, qname, symbols, refs, out);
}

function addInterfaceLens(
  i: InterfaceDecl,
  ownerQname: string,
  symbols: SymbolTable,
  refs: ReferenceIndex,
  out: CodeLens[]
): void {
  if (!i.source) return;
  const qname = `${ownerQname}.${i.name}`;
  const sym = symbols.resolve(qname, i.source.file);
  const count = sym ? refs.referencesTo(sym.id).length : 0;
  out.push({
    range: firstLine(i.source),
    command: {
      title: `${count} reference${count === 1 ? "" : "s"}`,
      command: "archlang.showDependents",
      arguments: [qname],
    },
  });
}

function firstLine(span: SourceSpan): Range {
  const lsp = toLspRange(span);
  return {
    start: lsp.start,
    end: { line: lsp.start.line, character: lsp.start.character + 1 },
  };
}
